import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/hooks/useLanguage";
import { resolvePageSeo, type SeoPageKey } from "@/i18n/seo";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  /** Optional Arabic variants — browser-side admin preview only. */
  titleAr?: string;
  descriptionAr?: string;
  keywordsAr?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

// Default organization schema
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ANAYA Perfume",
  description: "AI-Powered Personalized Fragrances - Create your signature scent",
  url: "https://www.anayaperfume.com",
  logo: "https://www.anayaperfume.com/pwa-512x512.png",
  sameAs: [
    "https://www.instagram.com/anayaperfume",
    "https://www.facebook.com/anayaperfume",
    "https://twitter.com/anayaperfume"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    availableLanguage: ["English", "Arabic"]
  }
};

// Website schema for search
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "ANAYA Perfume",
  url: "https://www.anayaperfume.com",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://www.anayaperfume.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const defaultMeta = {
  title: "ANAYA Perfume | AI-Powered Personalized Fragrances",
  description: "Create your signature scent with ANAYA's AI-powered perfume creation. Answer a few questions about your essence and receive three personalized fragrances crafted just for you.",
  keywords: "personalized perfume, custom fragrance, AI perfume, luxury perfume, ANAYA, signature scent, bespoke fragrance, Dubai perfume",
  image: "https://www.anayaperfume.com/social/anaya-share-v2-1200x630.jpg",
  url: "https://www.anayaperfume.com"
};

export const SEO = ({
  title,
  description,
  keywords,
  titleAr,
  descriptionAr,
  keywordsAr,
  image,
  url,
  type = "website",
  noIndex = false,
  jsonLd
}: SEOProps) => {
  const { language } = useLanguage();
  const isAr = language === "ar";
  // Per-field fallback: Arabic variants render only in the Arabic admin preview;
  // canonical URLs, share image and og:url always stay on the English routes.
  const baseTitle = isAr && titleAr?.trim() ? titleAr : title;
  const pageTitle = baseTitle
    ? `${baseTitle} | ANAYA Perfume`
    : defaultMeta.title;
  const pageDescription = (isAr && descriptionAr?.trim() ? descriptionAr : description) || defaultMeta.description;
  const pageKeywords = (isAr && keywordsAr?.trim() ? keywordsAr : keywords) || defaultMeta.keywords;
  const pageImage = image || defaultMeta.image;
  const pageUrl = url || defaultMeta.url;

  // Combine schemas - use type assertion for flexibility
  const schemas: Record<string, unknown>[] = [organizationSchema, websiteSchema];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      schemas.push(...jsonLd);
    } else {
      schemas.push(jsonLd);
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="ANAYA Perfume" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemas)}
      </script>
    </Helmet>
  );
};

// Pre-configured SEO for common pages. Titles/descriptions come from the
// bilingual dictionary (src/i18n/seo.ts) with per-field English fallback;
// canonical URLs and share images stay on the canonical English routes.
const LocalizedSEO = ({ pageKey, ...rest }: { pageKey: SeoPageKey } & Omit<SEOProps, "title" | "description" | "keywords" | "titleAr" | "descriptionAr" | "keywordsAr">) => {
  const { language } = useLanguage();
  const seo = resolvePageSeo(pageKey, language);
  return <SEO title={seo.title} description={seo.description} keywords={seo.keywords} {...rest} />;
};

export const HomeSEO = () => (
  <LocalizedSEO pageKey="home" url="https://www.anayaperfume.com" />
);

export const QuizSEO = () => (
  <LocalizedSEO pageKey="quiz" url="https://www.anayaperfume.com/quiz" />
);

export const CorporateGiftingSEO = () => (
  <LocalizedSEO
    pageKey="corporateGifting"
    url="https://www.anayaperfume.com/corporate-gifting"
    jsonLd={{
      "@context": "https://schema.org",
      "@type": "Service",
      name: "ANAYA Corporate Gifting",
      description: "Bespoke fragrance solutions for corporate events and VIP clients",
      provider: {
        "@type": "Organization",
        name: "ANAYA Perfume"
      }
    }}
  />
);

export const SpecialOccasionsSEO = () => (
  <LocalizedSEO pageKey="specialOccasions" url="https://www.anayaperfume.com/special-occasions" />
);

export const BlogSEO = () => (
  <LocalizedSEO pageKey="blog" url="https://www.anayaperfume.com/blog" type="website" />
);

export const ContactSEO = () => (
  <LocalizedSEO
    pageKey="contact"
    url="https://www.anayaperfume.com/contact"
    jsonLd={{
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact ANAYA Perfume",
      description: "Get in touch with our fragrance experts"
    }}
  />
);

export const HowItWorksSEO = () => (
  <LocalizedSEO
    pageKey="howItWorks"
    url="https://www.anayaperfume.com/how-it-works"
    jsonLd={{
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to Create Your Personalized ANAYA Perfume",
      description: "Steps to create your signature scent with ANAYA",
      step: [
        {
          "@type": "HowToStep",
          name: "Take the Quiz",
          text: "Answer questions about your personality and preferences"
        },
        {
          "@type": "HowToStep",
          name: "AI Creates Your Blend",
          text: "Our AI analyzes your responses to craft your unique fragrance"
        },
        {
          "@type": "HowToStep",
          name: "Receive Your Perfume",
          text: "Your personalized fragrance is delivered to your door"
        }
      ]
    }}
  />
);

export const AboutSEO = () => (
  <LocalizedSEO pageKey="about" url="https://www.anayaperfume.com/who-we-are" />
);

export default SEO;
